"use client";

import * as React from "react";
import { useState, useCallback } from "react";
import { Loader2, Shield } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";

export interface SignInFormProps {
  /** Product/app title shown in the card header (e.g. "Board", "Passport Admin") */
  title: string;
  /** Optional description below the title */
  description?: string;
  /** Optional logo/icon rendered above or beside the title */
  logo?: React.ReactNode;
  /** Called when the user submits the form. Should throw on failure. */
  onSubmit: (email: string, password: string) => Promise<void>;
  /** Optional href for a "Don't have an account?" register link */
  registerHref?: string;
  /** Label for the register link. Default: "Create an account" */
  registerLabel?: string;
  /** Optional href for a "Forgot your password?" link under the password field. Renders only when the local form is shown. */
  forgotPasswordHref?: string;
  /**
   * Router Link component for the register link (e.g. react-router-dom's Link).
   * Falls back to a plain <a> tag if not provided.
   */
  LinkComponent?: React.ComponentType<{
    to: string;
    className?: string;
    children: React.ReactNode;
  }>;
  /** Custom error message to display (overrides internal error state) */
  error?: string | null;
  className?: string;
  /** Whether to show the SSO / Passport login button */
  showSsoButton?: boolean;
  /** Callback when SSO button is clicked */
  onSsoClick?: () => void;
  /** Label for the SSO button. Default: "Continue with Trillion Passport" */
  ssoLabel?: string;
  /** Whether to show the local email/password form. Default: true */
  showLocalForm?: boolean;
  /** Hint message when user should use SSO (renders an info banner) */
  ssoHint?: string | null;
}

export function SignInForm({
  title,
  description,
  logo,
  onSubmit,
  registerHref,
  registerLabel = "Create an account",
  forgotPasswordHref,
  LinkComponent,
  error: externalError,
  className,
  showSsoButton,
  onSsoClick,
  ssoLabel,
  showLocalForm = true,
  ssoHint,
}: SignInFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  const error = externalError ?? internalError;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setInternalError(null);
      setIsLoading(true);

      try {
        await onSubmit(email, password);
      } catch (err) {
        setInternalError(
          err instanceof Error ? err.message : "Login failed"
        );
      } finally {
        setIsLoading(false);
      }
    },
    [email, password, onSubmit]
  );

  const RegisterLink = LinkComponent
    ? ({ children, className: cls }: { children: React.ReactNode; className?: string }) => (
        <LinkComponent to={registerHref!} className={cls}>{children}</LinkComponent>
      )
    : ({ children, className: cls }: { children: React.ReactNode; className?: string }) => (
        <a href={registerHref!} className={cls}>{children}</a>
      );

  const ForgotLink = LinkComponent
    ? ({ children, className: cls }: { children: React.ReactNode; className?: string }) => (
        <LinkComponent to={forgotPasswordHref!} className={cls}>{children}</LinkComponent>
      )
    : ({ children, className: cls }: { children: React.ReactNode; className?: string }) => (
        <a href={forgotPasswordHref!} className={cls}>{children}</a>
      );

  return (
    <div
      className={cn(
        "flex min-h-screen items-center justify-center bg-background p-4",
        className
      )}
    >
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          {logo && <div className="mb-2 flex justify-center">{logo}</div>}
          <CardTitle className="text-xl">Sign in to {title}</CardTitle>
          {description && (
            <CardDescription>{description}</CardDescription>
          )}
          {!description && (
            <CardDescription>
              Enter your email and password to access your{" "}
              {title.toLowerCase().includes("admin") ? "dashboard" : "diagrams"}
            </CardDescription>
          )}
        </CardHeader>

        <CardContent>
          <div className="flex flex-col gap-4">
            {error && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            {ssoHint && (
              <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
                {ssoHint}
              </div>
            )}

            {showSsoButton && (
              <Button
                type="button"
                variant="outline"
                className={cn("w-full", ssoHint && "ring-2 ring-primary ring-offset-2 ring-offset-card")}
                onClick={onSsoClick}
              >
                <Shield className="mr-2 size-4" />
                {ssoLabel ?? "Continue with Trillion Passport"}
              </Button>
            )}

            {showSsoButton && showLocalForm && (
              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>
            )}

            {showLocalForm !== false && (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="sign-in-email">Email</Label>
                  <Input
                    id="sign-in-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="sign-in-password">Password</Label>
                  <Input
                    id="sign-in-password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                    autoComplete="current-password"
                  />
                </div>

                {forgotPasswordHref && (
                  <div className="-mt-2 text-right">
                    <ForgotLink className="text-sm text-muted-foreground underline-offset-4 hover:underline">
                      Forgot your password?
                    </ForgotLink>
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign in"
                  )}
                </Button>
              </form>
            )}
          </div>
        </CardContent>

        {registerHref && (
          <CardFooter className="justify-center">
            <p className="text-sm text-muted-foreground">
              Don&apos;t have an account?{" "}
              <RegisterLink className="text-primary underline-offset-4 hover:underline">
                {registerLabel}
              </RegisterLink>
            </p>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
